import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { listUserFilters } from '#domain/core/filter/filter.query';
import { transformFilterFromSearch } from '#domain/core/filter/filter.utilities';
import { useUserStore } from '#stores/user.store';
import { type ProductFilterConfigType, type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, unref, type Ref } from 'vue';

interface TreeViewItem {
  nodeId: string;
}

export interface TreeView {
  children?: TreeViewItem[];
}

function sortSelectedItemsFirst(
  treeView: TreeView | undefined,
  selectedFilters: Record<string, unknown>
): TreeView | undefined {
  if (!treeView?.children) {
    return treeView;
  }

  const selectedNodeIds = new Set(Object.keys(selectedFilters));
  treeView.children.sort((a: TreeViewItem, b: TreeViewItem) => {
    const aSelected = selectedNodeIds.has(a.nodeId);
    const bSelected = selectedNodeIds.has(b.nodeId);

    if (aSelected === bSelected) {
      return 0;
    }
    return aSelected ? -1 : 1;
  });

  return treeView;
}

interface UseProductDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  productFilterConfigType: ProductFilterConfigType;
  taxonomy: Ref<ProductTaxonomyType>;
  applyCompetitorAnonymization?: boolean;
}

export function useProductData({
  debouncedSearch,
  enabled,
  productFilterConfigType,
  checkedItemsState,
  taxonomy,
  applyCompetitorAnonymization = false
}: UseProductDataProps) {
  const user = useUserStore();
  const { isAnonymized } = storeToRefs(user);

  const currentProductFilters = computed(() => {
    return Object.fromEntries(
      Object.entries(checkedItemsState.value[productFilterConfigType.filterName]).filter(
        ([, state]) => state.state === 'checked'
      )
    );
  });

  const {
    data: productData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [
      listUserFilters.name,
      applyCompetitorAnonymization ? 'competitor' : 'regular',
      debouncedSearch,
      taxonomy,
      currentProductFilters.value
    ],
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      return listUserFilters({
        filterType: productFilterConfigType.filterType,
        search: debouncedSearch.value,
        orNodeIdIn: Object.keys(currentProductFilters.value),
        searchInKey: true,
        limit: 100,
        taxonomy: unref(taxonomy)
      });
    },
    select: (result) => {
      if (result.status === 'success') {
        return result.data;
      }
    }
  });

  const itemsProducts = computed(() => {
    if (!productData.value || !enabled.value) {
      return undefined;
    }
    const shouldApplyClientSideAnonymization = isAnonymized.value && !applyCompetitorAnonymization;
    const data = shouldApplyClientSideAnonymization
      ? transformFilterFromSearch(productData.value, productFilterConfigType.anonymizationPattern)
      : productData.value;

    const treeView = filterToTreeViewItemWithDefaultValue({
      filter: data,
      defaultFilters: data,
      selectedFilters: currentProductFilters.value
    });

    return applyCompetitorAnonymization ? sortSelectedItemsFirst(treeView, currentProductFilters.value) : treeView;
  });

  const isLoadingProducts = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsProducts, isLoadingProducts, productFilterResponse: productData };
}
